import { useState, useEffect, useMemo, useRef } from 'react'
import { Cloud, CloudRain, Sun, CloudSun, Wind } from '@/ui/icons'
import { useAuth } from '@/modules/auth'
import type { WeatherData } from '@/shared/api/types'

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1'

interface TripWeatherLocation {
  id: string
  name: string
  latitude: number
  longitude: number
  poi_type: 'campsite' | 'meetup_spot'
  weather: WeatherData | null
}

interface TripWeatherForecastProps {
  locations: Array<{
    id: string
    name: string
    latitude: number
    longitude: number
    poi_type: 'campsite' | 'meetup_spot'
  }>
}

function getWeatherIcon(condition?: string) {
  if (!condition) return Cloud
  
  const conditionLower = condition.toLowerCase()
  if (conditionLower.includes('rain') || conditionLower.includes('drizzle')) return CloudRain
  if (conditionLower.includes('snow')) return Cloud // Using Cloud for snow since specific snow icon may not be available
  if (conditionLower.includes('clear') || conditionLower.includes('sun')) return Sun
  if (conditionLower.includes('cloud')) return CloudSun
  return Cloud
}

function formatTemperature(temp?: number): string {
  if (temp === undefined || temp === null) return '--'
  return `${Math.round(temp)}°F`
}

function getWeatherIconUrl(icon: string): string {
  return `https://openweathermap.org/img/wn/${icon}@2x.png`
}

export function TripWeatherForecast({ locations }: TripWeatherForecastProps) {
  const { getAccessToken } = useAuth()
  const [weatherData, setWeatherData] = useState<Map<string, WeatherData | null>>(new Map())
  const [isLoading, setIsLoading] = useState(true)
  const fetchingRef = useRef<Set<string>>(new Set())
  const previousLocationsRef = useRef<string>('')

  // Create a stable key for locations to detect actual changes
  const locationsKey = useMemo(() => {
    return JSON.stringify(
      locations.map(loc => ({
        id: loc.id,
        lat: loc.latitude,
        lon: loc.longitude
      })).sort((a, b) => a.id.localeCompare(b.id))
    )
  }, [locations])

  useEffect(() => {
    async function fetchWeather() {
      if (locations.length === 0) {
        setIsLoading(false)
        return
      }

      // Skip if locations haven't actually changed
      if (previousLocationsRef.current === locationsKey) {
        return
      }

      previousLocationsRef.current = locationsKey
      setIsLoading(true)
      const weatherMap = new Map<string, WeatherData | null>()
      const token = getAccessToken()
      fetchingRef.current.clear()

      // Deduplicate by coordinates to avoid fetching same location multiple times
      const coordinateMap = new Map<string, string[]>() // coord key -> location ids
      locations.forEach(location => {
        const coordKey = `${location.latitude},${location.longitude}`
        if (!coordinateMap.has(coordKey)) {
          coordinateMap.set(coordKey, [])
        }
        coordinateMap.get(coordKey)!.push(location.id)
      })

      // Fetch weather for unique coordinates in parallel
      await Promise.all(
        Array.from(coordinateMap.entries()).map(async ([coordKey, locationIds]) => {
          // Skip if already fetching this coordinate
          if (fetchingRef.current.has(coordKey)) {
            return
          }
          fetchingRef.current.add(coordKey)

          const [lat, lon] = coordKey.split(',').map(Number)
          
          try {
            const response = await fetch(
              `${API_BASE}/weather?lat=${lat}&lon=${lon}`,
              {
                headers: {
                  ...(token && { Authorization: `Bearer ${token}` }),
                },
              }
            )

            if (!response.ok) {
              throw new Error('Failed to fetch weather')
            }

            const data = await response.json()
            const weather = data.data || null
            
            // Set weather data for all locations with these coordinates
            locationIds.forEach(locationId => {
              weatherMap.set(locationId, weather)
            })
          } catch (error) {
            // Set null for all locations with these coordinates
            locationIds.forEach(locationId => {
              weatherMap.set(locationId, null)
            })
          } finally {
            fetchingRef.current.delete(coordKey)
          }
        })
      )

      setWeatherData(weatherMap)
      setIsLoading(false)
    }

    fetchWeather()
    // Only depend on locationsKey, not getAccessToken (call it inside effect)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationsKey])

  // Show placeholder when no locations
  if (locations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-6 text-center gap-2">
        <div className="h-10 w-10 rounded-full bg-earth-100 dark:bg-earth-800 flex items-center justify-center">
          <Cloud className="h-5 w-5 text-earth-400" aria-hidden />
        </div>
        <div>
          <p className="text-sm font-medium text-earth-700 dark:text-earth-300">No forecast locations</p>
          <p className="text-xs text-earth-500 dark:text-earth-500 mt-0.5">
            Add a campsite or meetup spot to see weather for your trip.
          </p>
        </div>
      </div>
    )
  }

  // Separate meetup spot from campsites
  const meetupLocation = locations.find(loc => loc.poi_type === 'meetup_spot')
  const campsites = locations.filter(loc => loc.poi_type !== 'meetup_spot')

  const weatherLocations: TripWeatherLocation[] = [
    ...(meetupLocation ? [{
      ...meetupLocation,
      weather: weatherData.get(meetupLocation.id) || null
    }] : []),
    ...campsites.map(loc => ({
      ...loc,
      weather: weatherData.get(loc.id) || null
    }))
  ].filter(Boolean) as TripWeatherLocation[]

  if (weatherLocations.length === 0) {
    return null
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 h-full flex flex-col min-h-[400px]">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Weather Forecast</h3>
        {weatherLocations.length > 0 && weatherLocations[0].poi_type === 'meetup_spot' && (
          <span className="text-sm text-gray-500 dark:text-gray-400">{weatherLocations[0].name}</span>
        )}
      </div>

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
            <div className="animate-pulse h-32 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            <div className="animate-pulse h-32 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            <div className="animate-pulse h-32 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {weatherLocations.map((location) => {
              const WeatherIcon = getWeatherIcon(location.weather?.condition)
              const isMeetup = location.poi_type === 'meetup_spot'
              const label = isMeetup ? 'Meetup Location' : location.name

              return (
                <div
                  key={location.id}
                  className={`p-4 rounded-lg border ${
                    isMeetup
                      ? 'border-adventure-200 bg-adventure-50 dark:border-adventure-800 dark:bg-adventure-950/40'
                      : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800'
                  }`}
                >
                  <div className="flex flex-col">
                    <div className="flex items-start gap-2 mb-2">
                      <WeatherIcon className="w-5 h-5 text-adventure-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <span
                          className="font-medium text-gray-900 dark:text-gray-100 text-sm block truncate"
                          title={label}
                        >
                          {label}
                        </span>
                      </div>
                      {isMeetup && (
                        <span className="text-xs px-2 py-0.5 bg-adventure-100 dark:bg-adventure-900 text-adventure-700 dark:text-adventure-300 rounded flex-shrink-0">
                          Start
                        </span>
                      )}
                    </div>
                    {location.weather ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                            {formatTemperature(location.weather.temp)}
                          </div>
                          <img
                            src={getWeatherIconUrl(location.weather.icon)}
                            alt={location.weather.description}
                            className="w-12 h-12"
                          />
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 capitalize truncate">
                          {location.weather.description}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                          <div>Feels like {formatTemperature(location.weather.feels_like)}</div>
                          <div>Wind {Math.round(location.weather.wind_speed)} mph</div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500 dark:text-gray-400">Weather data unavailable</div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {weatherLocations.some(loc => loc.weather) && (
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg flex items-start space-x-2 mt-auto">
              <Wind className="w-4 h-4 text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {weatherLocations.length === 1
                  ? 'Check weather conditions for planning your gear and activities.'
                  : 'Weather conditions vary by location. Plan accordingly for each campsite.'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

